import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Search, FileText, XCircle, Unlock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGroups = ({ headers, onUpdate }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionId, setActionId] = useState(null);
  const [showRename, setShowRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [showDelete, setShowDelete] = useState(null);
  const [showForceRelease, setShowForceRelease] = useState(null); // group id (layer 1)
  const [forceReleaseStep, setForceReleaseStep] = useState(1); // 1 = reason, 2 = confirmation
  const [forceReason, setForceReason] = useState('');
  const [forceConfirmation, setForceConfirmation] = useState('');

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/groups`, { headers });
      setGroups(res.data);
    } catch {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const doRename = async (gid) => {
    if (!newName.trim()) { toast.error('Name required'); return; }
    setActionId(gid);
    try {
      await axios.put(`${API_URL}/api/admin/groups/${gid}/rename`, { new_name: newName.trim() }, { headers });
      toast.success('Group renamed');
      setShowRename(null);
      setNewName('');
      fetchGroups();
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setActionId(null);
    }
  };

  const doDelete = async (gid) => {
    setActionId(gid);
    try {
      await axios.delete(`${API_URL}/api/admin/groups/${gid}`, { headers });
      toast.success('Group deleted');
      setShowDelete(null);
      fetchGroups();
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setActionId(null);
    }
  };

  const openForceRelease = (gid) => {
    setShowForceRelease(gid);
    setForceReleaseStep(1);
    setForceReason('');
    setForceConfirmation('');
  };

  const closeForceRelease = () => {
    setShowForceRelease(null);
    setForceReleaseStep(1);
    setForceReason('');
    setForceConfirmation('');
  };

  const doForceRelease = async (gid) => {
    setActionId(gid);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/groups/${gid}/force-release`,
        { confirmation_code: forceConfirmation, reason: forceReason },
        { headers }
      );
      toast.success(res.data.message || 'Funds released');
      closeForceRelease();
      fetchGroups();
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to release funds');
    } finally {
      setActionId(null);
    }
  };

  const filtered = groups.filter(g => {
    const s = searchTerm.toLowerCase();
    return !s || (g.name || '').toLowerCase().includes(s);
  });

  const statusColor = {
    forming: 'bg-blue-100 text-blue-700',
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-slate-100 text-slate-700',
    paused: 'bg-amber-100 text-amber-700',
  };

  return (
    <div className="space-y-4">
      {/* Groups List */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2">
            <Users size={18} className="text-[#2272C5]" />Groups ({groups.length})
          </CardTitle>
          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search groups..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm"
              data-testid="group-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-20 rounded-xl" /> : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-center text-slate-500 py-4 text-sm">No groups found</p>
              ) : filtered.map(g => (
                <div key={g.id} className="p-4 rounded-xl border-2 border-slate-200 bg-slate-50" data-testid={`admin-group-${g.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-[#0B1F3A] text-sm">{g.name}</p>
                      <p className="text-[10px] text-slate-500">{g.group_size} members &middot; {g.contribution_frequency}</p>
                    </div>
                    <Badge className={`${statusColor[g.status] || 'bg-slate-100 text-slate-700'} text-[9px]`}>
                      {g.status}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-2">
                    {showRename === g.id ? (
                      <div className="flex-1 space-y-2">
                        <input
                          value={newName}
                          onChange={e => setNewName(e.target.value)}
                          placeholder="New group name..."
                          className="w-full px-3 py-1.5 rounded-lg border border-[#2272C5] text-xs"
                          data-testid={`rename-group-input-${g.id}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-[#2272C5] hover:bg-[#1a5ea0] text-white text-xs"
                            onClick={() => doRename(g.id)}
                            disabled={actionId === g.id}
                            data-testid={`confirm-rename-group-${g.id}`}
                          >Rename</Button>
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowRename(null); setNewName(''); }}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs text-[#2272C5]"
                        onClick={() => { setShowRename(g.id); setNewName(g.name || ''); }}
                        data-testid={`rename-group-${g.id}`}
                      >
                        <FileText size={12} className="mr-1" />Rename
                      </Button>
                    )}

                    {showDelete === g.id ? (
                      <div className="flex-1 space-y-2">
                        <p className="text-xs text-red-600 font-semibold">Delete this group and all its data? This cannot be undone.</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            className="text-xs"
                            onClick={() => doDelete(g.id)}
                            disabled={actionId === g.id}
                            data-testid={`confirm-delete-group-${g.id}`}
                          >Delete Forever</Button>
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowDelete(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs text-red-600 border-red-300"
                        onClick={() => setShowDelete(g.id)}
                        data-testid={`delete-group-${g.id}`}
                      >
                        <XCircle size={12} className="mr-1" />Delete
                      </Button>
                    )}

                    {/* Force Release – 2-layer override */}
                    {showForceRelease !== g.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs text-amber-700 border-amber-300"
                        onClick={() => openForceRelease(g.id)}
                        data-testid={`force-release-btn-${g.id}`}
                      >
                        <Unlock size={12} className="mr-1" />Force Release Funds
                      </Button>
                    )}
                  </div>

                  {showForceRelease === g.id && (
                    <div className="mt-3 p-3 rounded-xl border-2 border-amber-300 bg-amber-50 space-y-2" data-testid={`force-release-panel-${g.id}`}>
                      <p className="text-xs font-black text-amber-800">
                        ⚠️ Two-Layer Override: Release all locked funds & cancel group
                      </p>
                      {forceReleaseStep === 1 ? (
                        <>
                          <p className="text-[11px] text-amber-700 font-semibold">
                            Layer 1 of 2 — State a reason for the manual override.
                          </p>
                          <textarea
                            value={forceReason}
                            onChange={(e) => setForceReason(e.target.value)}
                            placeholder="E.g., Group stuck, members requested refund, support ticket #..."
                            className="w-full px-3 py-2 rounded-lg border border-amber-400 text-xs font-semibold"
                            rows={2}
                            data-testid={`force-release-reason-${g.id}`}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold"
                              onClick={() => {
                                if (!forceReason.trim()) { toast.error('Reason is required'); return; }
                                setForceReleaseStep(2);
                              }}
                              data-testid={`force-release-next-${g.id}`}
                            >Continue</Button>
                            <Button size="sm" variant="outline" className="text-xs" onClick={closeForceRelease} data-testid={`force-release-cancel-${g.id}`}>Cancel</Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-[11px] text-amber-700 font-semibold">
                            Layer 2 of 2 — Type the exact group name to confirm. This will release ALL locked funds (security holds + lock deposits) to members and cancel the group.
                          </p>
                          <p className="text-[10px] text-slate-700">Group name: <span className="font-black">{g.name}</span></p>
                          <input
                            value={forceConfirmation}
                            onChange={(e) => setForceConfirmation(e.target.value)}
                            placeholder="Type the group name exactly"
                            className="w-full px-3 py-2 rounded-lg border border-amber-400 text-xs font-semibold"
                            data-testid={`force-release-confirmation-${g.id}`}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold"
                              onClick={() => doForceRelease(g.id)}
                              disabled={actionId === g.id || forceConfirmation.trim().toLowerCase() !== (g.name || '').trim().toLowerCase()}
                              data-testid={`force-release-confirm-${g.id}`}
                            >{actionId === g.id ? 'Releasing…' : 'Release & Cancel'}</Button>
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => setForceReleaseStep(1)} data-testid={`force-release-back-${g.id}`}>Back</Button>
                            <Button size="sm" variant="outline" className="text-xs" onClick={closeForceRelease}>Cancel</Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGroups;
